export type AssessmentDimension =
  | 'workflowFit'
  | 'humanControl'
  | 'executionReadiness'
  | 'learningReadiness';

export type WorkflowRoute =
  | 'AI-assisted workflow'
  | 'Hybrid workflow'
  | 'Deterministic automation'
  | 'Human-led workflow';

export interface AssessmentOption {
  value: string;
  label: string;
  description: string;
  points: number;
}

export interface AssessmentQuestion {
  id: string;
  section: number;
  dimension: AssessmentDimension;
  prompt: string;
  guidance: string;
  options: AssessmentOption[];
}

export interface DimensionResult {
  label: string;
  score: number;
  interpretation: string;
}

export interface DimensionPairEntry {
  dimension: AssessmentDimension;
  label: string;
  score: number;
}

export interface QuickScoreResult {
  grade: 'A' | 'B' | 'C' | 'D' | 'E';
  readinessLabel: string;
  overallScore: number;
  opportunityFitScore: number;
  executionReadinessScore: number;
  route: WorkflowRoute;
  routeRationale: string;
  confidence: 'Directional';
  dimensions: Record<AssessmentDimension, DimensionResult>;
  strengths: string[];
  priorities: string[];
  strongestDimension: DimensionPairEntry;
  weakestDimension: DimensionPairEntry;
  gradeChangeLine: string;
  evidenceChecklist: string[];
  nextStep: string;
  limitations: string[];
}

export const assessmentSections = [
  {
    number: 1,
    label: 'Workflow fit',
    description: 'Identify whether the work contains the ambiguity, repetition, and known outputs that make AI useful.'
  },
  {
    number: 2,
    label: 'Human control',
    description: 'Define who remains accountable, how errors are caught, and where exceptions go.'
  },
  {
    number: 3,
    label: 'Execution readiness',
    description: 'Check whether the team can access the inputs, connect the systems, and own the pilot.'
  },
  {
    number: 4,
    label: 'Learning loop',
    description: 'Confirm that corrections and outcomes can improve the workflow over time.'
  }
] as const;

export const quickScoreQuestions: AssessmentQuestion[] = [
  {
    id: 'input-shape',
    section: 1,
    dimension: 'workflowFit',
    prompt: 'What does the team usually receive at the start of this workflow?',
    guidance: 'Think about the normal case, not the cleanest example.',
    options: [
      { value: 'structured', label: 'Structured fields', description: 'Consistent forms, database records, or fixed system fields.', points: 3 },
      { value: 'mixed', label: 'A mix of structured and unstructured inputs', description: 'Forms plus emails, attachments, notes, or comments.', points: 9 },
      { value: 'unstructured', label: 'Mostly unstructured information', description: 'Emails, PDFs, images, transcripts, or free-form requests.', points: 10 },
      { value: 'physical', label: 'Mostly physical or live interaction', description: 'In-person observation, hands-on work, or live conversation.', points: 3 }
    ]
  },
  {
    id: 'output-shape',
    section: 1,
    dimension: 'workflowFit',
    prompt: 'What should the workflow produce?',
    guidance: 'Choose the output employees are responsible for delivering.',
    options: [
      { value: 'calculation', label: 'An exact calculation or rules result', description: 'The same inputs should always produce the same answer.', points: 2 },
      { value: 'structured-draft', label: 'A known set of fields or a first draft', description: 'The format is known, but the source requires interpretation.', points: 10 },
      { value: 'comparison', label: 'A comparison, summary, or recommendation', description: 'The work weighs context before a person decides.', points: 9 },
      { value: 'final-decision', label: 'A final high-impact decision', description: 'The output directly approves, rejects, diagnoses, or commits.', points: 3 }
    ]
  },
  {
    id: 'weekly-volume',
    section: 1,
    dimension: 'workflowFit',
    prompt: 'How often does the team perform this workflow?',
    guidance: 'Use a reasonable weekly average across everyone involved.',
    options: [
      { value: 'under-10', label: 'Fewer than 10 times per week', description: 'Low repetition or highly episodic work.', points: 2 },
      { value: '10-50', label: '10–50 times per week', description: 'Repeated often enough to establish a baseline.', points: 6 },
      { value: '51-200', label: '51–200 times per week', description: 'Meaningful repetition and measurable operating effort.', points: 8 },
      { value: 'over-200', label: 'More than 200 times per week', description: 'High-volume work with material aggregate impact.', points: 10 }
    ]
  },
  {
    id: 'normal-path',
    section: 1,
    dimension: 'workflowFit',
    prompt: 'How much of the work follows a recognizable normal path?',
    guidance: 'Exceptions can remain human-led. Estimate the share that is repeatable.',
    options: [
      { value: 'under-30', label: 'Less than 30%', description: 'Most cases are unique or require deep situational judgment.', points: 2 },
      { value: '30-50', label: 'About 30–50%', description: 'A repeatable core exists, but exceptions are common.', points: 5 },
      { value: '51-70', label: 'About 51–70%', description: 'Most cases share a useful pattern.', points: 8 },
      { value: 'over-70', label: 'More than 70%', description: 'A strong normal path can be separated from exceptions.', points: 10 }
    ]
  },
  {
    id: 'review-owner',
    section: 2,
    dimension: 'humanControl',
    prompt: 'Who would be accountable for reviewing AI-prepared work?',
    guidance: 'The reviewer should have authority to correct the output and handle exceptions.',
    options: [
      { value: 'unclear', label: 'No role is defined yet', description: 'Ownership would need to be established before a pilot.', points: 2 },
      { value: 'manager', label: 'A manager could spot-check it', description: 'Oversight exists, but routine review is not assigned.', points: 5 },
      { value: 'operator', label: 'A trained operator already owns the result', description: 'The person doing the work can confirm, correct, and escalate.', points: 10 },
      { value: 'none', label: 'The goal is to remove human review', description: 'No employee would routinely confirm the output.', points: 0 }
    ]
  },
  {
    id: 'error-tolerance',
    section: 2,
    dimension: 'humanControl',
    prompt: 'What happens when the prepared output is wrong?',
    guidance: 'Assess the consequence before a person has a chance to review it.',
    options: [
      { value: 'critical', label: 'A single error can cause serious harm', description: 'Safety, legal, financial, employment, or clinical consequences.', points: 2 },
      { value: 'costly', label: 'Errors are costly but reversible', description: 'A mistake creates rework, delay, or customer impact.', points: 6 },
      { value: 'contained', label: 'Errors stay inside a review step', description: 'A person can correct the draft before it affects another system.', points: 10 },
      { value: 'low-impact', label: 'Errors have limited operational impact', description: 'The output is advisory or easy to discard.', points: 8 }
    ]
  },
  {
    id: 'exception-owner',
    section: 2,
    dimension: 'humanControl',
    prompt: 'How are unusual or incomplete cases handled today?',
    guidance: 'A strong pilot keeps a visible path for exceptions.',
    options: [
      { value: 'unclear', label: 'It varies by person', description: 'There is no shared escalation or exception path.', points: 2 },
      { value: 'informal', label: 'The team handles them informally', description: 'People know what to do, but the pattern is not documented.', points: 6 },
      { value: 'defined', label: 'A named role and path are defined', description: 'Exceptions have an owner, criteria, and escalation route.', points: 10 },
      { value: 'automate', label: 'The system should handle every case', description: 'The intended design does not preserve an exception lane.', points: 0 }
    ]
  },
  {
    id: 'workflow-owner',
    section: 3,
    dimension: 'executionReadiness',
    prompt: 'Does this workflow have an accountable business owner?',
    guidance: 'The owner needs authority to define success and approve operating changes.',
    options: [
      { value: 'none', label: 'No clear owner', description: 'Responsibility is distributed or uncertain.', points: 2 },
      { value: 'informal', label: 'An informal owner', description: 'Someone leads the work without formal authority or baseline data.', points: 5 },
      { value: 'named', label: 'A named owner', description: 'One person can make scope and process decisions.', points: 8 },
      { value: 'owner-baseline', label: 'A named owner with baseline measures', description: 'Ownership and current performance are already visible.', points: 10 }
    ]
  },
  {
    id: 'source-access',
    section: 3,
    dimension: 'executionReadiness',
    prompt: 'Can IT provide representative examples of the inputs and outcomes?',
    guidance: 'Use redacted or synthetic samples where sensitive information is involved.',
    options: [
      { value: 'unknown', label: 'Access is unknown', description: 'The source, ownership, or retention is not yet confirmed.', points: 2 },
      { value: 'manual', label: 'A few examples can be gathered manually', description: 'Enough for discovery, but not yet for repeatable evaluation.', points: 5 },
      { value: 'export', label: 'Historical examples can be exported', description: 'Inputs and final outcomes can be assembled for a pilot.', points: 8 },
      { value: 'linked', label: 'Inputs, corrections, and outcomes are linkable', description: 'The team can build an evaluation set and trace results.', points: 10 }
    ]
  },
  {
    id: 'integration-path',
    section: 3,
    dimension: 'executionReadiness',
    prompt: 'How could a pilot connect to the systems involved?',
    guidance: 'A manual pilot is valid when it proves the workflow before integration work.',
    options: [
      { value: 'unknown', label: 'The connection path is unknown', description: 'IT has not yet reviewed the systems or access model.', points: 2 },
      { value: 'manual', label: 'Manual upload and review', description: 'A person can provide inputs and transfer approved results.', points: 6 },
      { value: 'import-export', label: 'Supported import or export', description: 'Files or built-in connectors can move approved records.', points: 8 },
      { value: 'api', label: 'Documented API or workflow connector', description: 'Authenticated integration is available for a bounded pilot.', points: 10 }
    ]
  },
  {
    id: 'correction-record',
    section: 4,
    dimension: 'learningReadiness',
    prompt: 'Can the team retain what the system suggested and what the employee changed?',
    guidance: 'Corrections are how you measure quality and improve the workflow.',
    options: [
      { value: 'none', label: 'No correction record exists', description: 'Only the final result is retained.', points: 1 },
      { value: 'notes', label: 'Corrections appear in notes or messages', description: 'Some evidence exists, but it is difficult to analyze.', points: 5 },
      { value: 'before-after', label: 'Before-and-after values can be stored', description: 'Suggestions and final values can be compared.', points: 8 },
      { value: 'structured', label: 'Corrections and reasons can be structured', description: 'The team can analyze error types, exceptions, and outcomes.', points: 10 }
    ]
  },
  {
    id: 'data-controls',
    section: 4,
    dimension: 'learningReadiness',
    prompt: 'Are the data boundaries for this workflow understood?',
    guidance: 'Consider personal, financial, health, confidential, and customer information.',
    options: [
      { value: 'unknown', label: 'The data has not been classified', description: 'The team cannot yet state what may be processed or retained.', points: 2 },
      { value: 'sensitive', label: 'Sensitive data is present; controls are undecided', description: 'The use case needs a privacy and provider review.', points: 4 },
      { value: 'documented', label: 'Sensitive fields and controls are documented', description: 'Access, retention, and approved processing are defined.', points: 9 },
      { value: 'low-risk', label: 'Inputs are low-risk or can be reliably redacted', description: 'A bounded evaluation can avoid sensitive records.', points: 10 }
    ]
  }
];

export const sampleQuickScoreAnswers: Record<string, string> = {
  'input-shape': 'mixed',
  'output-shape': 'structured-draft',
  'weekly-volume': '51-200',
  'normal-path': 'over-70',
  'review-owner': 'operator',
  'error-tolerance': 'contained',
  'exception-owner': 'defined',
  'workflow-owner': 'owner-baseline',
  'source-access': 'export',
  'integration-path': 'api',
  'correction-record': 'before-after',
  'data-controls': 'documented'
};

const dimensionLabels: Record<AssessmentDimension, string> = {
  workflowFit: 'Workflow AI fit',
  humanControl: 'Human control',
  executionReadiness: 'Execution readiness',
  learningReadiness: 'Learning and governance'
};

const dimensionInterpretation = (dimension: AssessmentDimension, score: number): string => {
  if (dimension === 'workflowFit') {
    if (score >= 75) return 'The normal path contains strong AI-assist signals.';
    if (score >= 50) return 'A useful opportunity may exist, but the scope needs narrowing.';
    return 'The workflow is currently a better candidate for process or rules-based improvement.';
  }
  if (dimension === 'humanControl') {
    if (score >= 75) return 'Accountability and exception handling are well positioned for a pilot.';
    if (score >= 50) return 'Human oversight exists but needs clearer operating rules.';
    return 'Define review authority and the exception lane before introducing AI.';
  }
  if (dimension === 'executionReadiness') {
    if (score >= 75) return 'The owner, evidence, and connection path can support a bounded pilot.';
    if (score >= 50) return 'Discovery can begin while specific technical prerequisites are closed.';
    return 'Ownership, sample access, or integration proof should come first.';
  }
  if (score >= 75) return 'The team can evaluate corrections and improve the workflow over time.';
  if (score >= 50) return 'Some learning evidence exists, but it is not yet analysis-ready.';
  return 'Create a correction record and confirm data boundaries before scaling.';
};

const getOption = (questionId: string, value: string | undefined): AssessmentOption | undefined =>
  quickScoreQuestions.find((question) => question.id === questionId)?.options.find((option) => option.value === value);

const scoreDimension = (answers: Record<string, string>, dimension: AssessmentDimension): number => {
  const questions = quickScoreQuestions.filter((question) => question.dimension === dimension);
  const earned = questions.reduce((sum, question) => sum + (getOption(question.id, answers[question.id])?.points ?? 0), 0);
  return Math.round((earned / (questions.length * 10)) * 100);
};

const gradeFor = (score: number): QuickScoreResult['grade'] => {
  if (score >= 80) return 'A';
  if (score >= 65) return 'B';
  if (score >= 50) return 'C';
  if (score >= 35) return 'D';
  return 'E';
};

const readinessLabelFor = (grade: QuickScoreResult['grade']): string => ({
  A: 'Pilot-ready foundation',
  B: 'Promising with defined prerequisites',
  C: 'Opportunity present; foundation gaps remain',
  D: 'Strengthen the workflow before piloting',
  E: 'Reroute before investing in AI'
}[grade]);

const determineRoute = (answers: Record<string, string>): { route: WorkflowRoute; rationale: string } => {
  const input = answers['input-shape'];
  const output = answers['output-shape'];
  const normalPath = answers['normal-path'];
  const errorTolerance = answers['error-tolerance'];

  if (input === 'structured' && output === 'calculation') {
    return {
      route: 'Deterministic automation',
      rationale: 'The input and output are governed by known rules. Use conventional software for the calculation, then add AI only where interpretation is genuinely required.'
    };
  }

  if (output === 'final-decision' || normalPath === 'under-30' || errorTolerance === 'critical') {
    return {
      route: 'Human-led workflow',
      rationale: 'The work is too consequential or variable for AI to own the result. AI may support research or drafting, but employee judgment must remain the operating centre.'
    };
  }

  if ((input === 'mixed' || input === 'unstructured') && (output === 'structured-draft' || output === 'comparison')) {
    return {
      route: 'AI-assisted workflow',
      rationale: 'The workflow converts variable information into a known draft or comparison. AI can prepare the repeatable work while deterministic checks and employees protect the decision.'
    };
  }

  return {
    route: 'Hybrid workflow',
    rationale: 'The opportunity combines structured steps, interpretation, and human judgment. Separate the workflow into deterministic, AI-assisted, and employee-owned lanes.'
  };
};

export const calculateQuickScore = (answers: Record<string, string>): QuickScoreResult => {
  const dimensionScores = {
    workflowFit: scoreDimension(answers, 'workflowFit'),
    humanControl: scoreDimension(answers, 'humanControl'),
    executionReadiness: scoreDimension(answers, 'executionReadiness'),
    learningReadiness: scoreDimension(answers, 'learningReadiness')
  };

  const opportunityFitScore = dimensionScores.workflowFit;
  const executionReadinessScore = Math.round(
    dimensionScores.humanControl * 0.4 +
    dimensionScores.executionReadiness * 0.35 +
    dimensionScores.learningReadiness * 0.25
  );
  const overallScore = Math.round(opportunityFitScore * 0.45 + executionReadinessScore * 0.55);
  const grade = gradeFor(overallScore);
  const route = determineRoute(answers);

  const dimensions = (Object.keys(dimensionScores) as AssessmentDimension[]).reduce(
    (result, dimension) => ({
      ...result,
      [dimension]: {
        label: dimensionLabels[dimension],
        score: dimensionScores[dimension],
        interpretation: dimensionInterpretation(dimension, dimensionScores[dimension])
      }
    }),
    {} as Record<AssessmentDimension, DimensionResult>
  );

  const strengths: string[] = [];
  const priorities: string[] = [];

  if (dimensionScores.workflowFit >= 70) strengths.push('A repeatable normal path can be separated from exceptions.');
  else priorities.push('Narrow the scope to one repeatable path with a known output.');

  if (dimensionScores.humanControl >= 70) strengths.push('Employee review and exception ownership can remain explicit.');
  else priorities.push('Name the reviewer, decision authority, and exception path.');

  if (dimensionScores.executionReadiness >= 70) strengths.push('A business owner and usable technical path are available for validation.');
  else priorities.push('Confirm the workflow owner, sample access, and connection method.');

  if (dimensionScores.learningReadiness >= 70) strengths.push('Corrections and data boundaries can support a measurable learning loop.');
  else priorities.push('Retain suggested values, employee changes, reasons, and final outcomes.');

  const gapForDimension: Record<AssessmentDimension, string> = {
    workflowFit: 'Narrow the scope to one repeatable path with a known output.',
    humanControl: 'Name the reviewer, decision authority, and exception path.',
    executionReadiness: 'Confirm the workflow owner, sample access, and connection method.',
    learningReadiness: 'Retain suggested values, employee changes, reasons, and final outcomes.'
  };

  const dimensionRank = Object.keys(dimensionScores) as AssessmentDimension[];
  const strongestKey = dimensionRank.reduce((a, b) => (dimensionScores[b] > dimensionScores[a] ? b : a));
  const weakestKey = dimensionRank.reduce((a, b) => (dimensionScores[b] <= dimensionScores[a] ? b : a));
  const strongestDimension: DimensionPairEntry = {
    dimension: strongestKey,
    label: dimensionLabels[strongestKey],
    score: dimensionScores[strongestKey]
  };
  const weakestDimension: DimensionPairEntry = {
    dimension: weakestKey,
    label: dimensionLabels[weakestKey],
    score: dimensionScores[weakestKey]
  };

  const weakestGap = gapForDimension[weakestKey];
  const gradeChangeLine = dimensionScores[weakestKey] < 70
    ? `What would change your grade: ${weakestGap.charAt(0).toLowerCase()}${weakestGap.slice(1)}`
    : 'What would change your grade: keep the current controls and verify them with fresh representative evidence.';

  const nextStepByRoute: Record<WorkflowRoute, string> = {
    'AI-assisted workflow': 'Start with a narrow pilot: validate 25–50 representative cases, keep employee approval on every result, and measure review time, corrections, exceptions, and downstream quality. Redesign the step so the employee sees the source, the draft, and one clear approve-or-fix action.',
    'Hybrid workflow': 'Begin with a phased rollout: map the workflow into rules-based, AI-assisted, and employee-owned steps before choosing technology or estimating value. Redesign each handoff so the next owner receives the source, the prior result, and the reason for any change.',
    'Deterministic automation': 'Start with a narrow pilot: document the rules and edge cases, automate them with conventional software, and reserve AI for any remaining unstructured interpretation. Redesign the step so invalid data is rejected with a stated reason and a named owner.',
    'Human-led workflow': 'Put foundations first: standardize the process and collect baseline evidence first. Reassess only a narrow drafting or research step where errors remain reviewable. Redesign that one step so the draft, the sources, and the reviewer decision are all recorded.'
  };

  const evidenceChecklistByRoute: Record<WorkflowRoute, string[]> = {
    'AI-assisted workflow': [
      'Gather 25–50 representative cases, including messy and incomplete ones.',
      'Keep employee approval on every result during the check.',
      'Record review time, corrections, exceptions, and downstream quality.'
    ],
    'Hybrid workflow': [
      'Split the workflow into rules-based, AI-assisted, and employee-owned steps.',
      'Name the owner and the handoff for each step.',
      'Choose technology only after the split is agreed.'
    ],
    'Deterministic automation': [
      'Write down the rules and the edge cases in plain words.',
      'Automate the rules with conventional software first.',
      'Reserve AI only for the leftover unstructured interpretation.'
    ],
    'Human-led workflow': [
      'Write down the standard process for the workflow.',
      'Collect baseline evidence: time, errors, and rework as they stand today.',
      'Reassess only one narrow drafting or research step where errors stay reviewable.'
    ]
  };

  return {
    grade,
    readinessLabel: readinessLabelFor(grade),
    overallScore,
    opportunityFitScore,
    executionReadinessScore,
    route: route.route,
    routeRationale: route.rationale,
    confidence: 'Directional',
    dimensions,
    strengths,
    priorities,
    strongestDimension,
    weakestDimension,
    gradeChangeLine,
    evidenceChecklist: evidenceChecklistByRoute[route.route],
    nextStep: nextStepByRoute[route.route],
    limitations: [
      'This Quick Score reflects one respondent and does not independently verify technical claims.',
      'It is a decision aid, not a certification, compliance opinion, or industry percentile.',
      'Financial value requires a measured baseline, expected adoption, implementation cost, and observed pilot results.',
      'Data readiness is per-workflow, never in general: a clean dataset elsewhere says nothing about this workflow.'
    ]
  };
};
